import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { toast } from "sonner";

const DISMISSED_KEY = "istres_pwa_banner_dismissed_at";
const DISMISS_DAYS = 7;

/**
 * Bandeau d'installation PWA — top header fin.
 * Clic = install natif direct (Chrome/Edge/Android). iOS = toast court (limitation Apple).
 */
export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(display-mode: standalone)").matches) setInstalled(true);
    if (window.navigator?.standalone === true) setInstalled(true);
    try {
      const at = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
      if (at && Date.now() - at < DISMISS_DAYS * 24 * 3600 * 1000) setDismissed(true);
    } catch (_) { /* */ }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const handleInstall = async () => {
    if (deferred) {
      // Chrome / Edge / Android : dialog natif direct, en 1 clic
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else if (isIOS) {
      // iOS Safari ne permet PAS d'installer en JS (restriction Apple) — toast court
      toast("Sur iPhone : appuyez sur ⎘ Partager puis « Sur l'écran d'accueil »", { duration: 6000 });
    } else {
      toast("Installation : utilisez le menu de votre navigateur → « Installer l'application »", { duration: 6000 });
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (_) { /* */ }
    setDismissed(true);
  };

  if (installed || dismissed) return null;

  return (
    <div
      className="relative z-50 bg-gradient-to-r from-plum-800 via-plum-700 to-pink-700 text-cream-50 text-[12px] sm:text-[13px] shadow-md"
      data-testid="pwa-install-banner"
      role="region"
      aria-label="Installer l'application"
    >
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 h-9 sm:h-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
          <Smartphone size={14} strokeWidth={2.4} className="shrink-0 text-sun-300" />
          <span className="font-semibold truncate">
            <span className="hidden sm:inline">Installez l&apos;app Istres &amp; Moi pour un accès rapide depuis votre écran d&apos;accueil</span>
            <span className="sm:hidden">Installez Istres &amp; Moi sur votre téléphone</span>
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button
            onClick={handleInstall}
            data-testid="pwa-install-banner-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-sun-400 hover:bg-sun-300 text-plum-900 font-bold text-[11px] sm:text-[12px] px-3 sm:px-4 py-1 sm:py-1.5 transition-colors"
          >
            <Download size={12} strokeWidth={2.6} />
            Installer
          </button>
          <button
            onClick={handleDismiss}
            data-testid="pwa-install-banner-close"
            aria-label="Masquer le bandeau d'installation"
            className="p-1 rounded-full hover:bg-white/15 transition-colors"
          >
            <X size={14} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>
  );
}
